import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import * as AlfredoComponents from "./";
import api from "../services/api.service";
import * as Styles from "../styles/AddProductOrder.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";

interface ComboOption {
  productUuid: string;
  productCode: number;
  productName: string;
  productPrice: string;
}

interface ProductGroupOptions {
  productGroup: string;
  options: ComboOption[];
}

interface AddProductOrderModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const AddProductOrderModal: React.FC<AddProductOrderModalProps> = ({
  isOpen,
  onRequestClose,
}) => {
  /**
   * =================================================================
   * Hooks do Zustand (store) para obter e definir dados necessários
   * =================================================================
   */
  const {
    subsidiaryDataZustand,
    setOrderDataZustand, // se precisar futuramente
    orderDataZustand,
    authUserZustand,    // se precisar futuramente
    setMessageZustand,
  } = useCombinedStore((state) => ({
    setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
    subsidiaryDataZustand: state.subsidiaryDataZustand,
    setOrderDataZustand: state.setOrderDataZustand,
    orderDataZustand: state.orderDataZustand,
    setMessageZustand: state.setMessageZustand,
    authUserZustand: state.authUserZustand,
  }));

  /**
   * ================================================================
   * Referências de elementos (usadas para focar inputs e outros usos)
   * ================================================================
   */
  const productNameInputRef = useRef<HTMLInputElement>(null);
  const productCodeInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const peopleInputRef = useRef<HTMLInputElement>(null);
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const modalComboWrapperRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  /**
   * ================================================================
   * Estados gerais do componente
   * ================================================================
   */
  // Filtro de nome e código
  const [productNameFilter, setProductNameFilter] = useState<string>("");
  const [productCodeFilter, setProductCodeFilter] = useState<string>("");

  // Quantidade e para qual pessoa (em caso de MESAS)
  const [quantity, setQuantity] = useState<number>(1);
  const [people, setPeople] = useState<string>("1");

  // Controle de submissão para evitar duplo envio
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Placeholders para autocomplete
  const [productNamePlaceholder, setProductNamePlaceholder] = useState<string>("");
  const [productCodePlaceholder, setProductCodePlaceholder] = useState<string>("");

  // Estados para combos
  const [showComboModal, setShowComboModal] = useState<boolean>(false);
  const [comboOptions, setComboOptions] = useState<ProductGroupOptions[]>([]);
  const [selectedComboOptions, setSelectedComboOptions] = useState<{
    [key: string]: string;
  }>({});
  const [currentComboIndex, setCurrentComboIndex] = useState<number>(0);

  // Estado para rastrear a linha selecionada na tabela
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  // Estado para o índice selecionado dentro do modal de combo
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  /**
   * ================================================================
   * Dados de produtos vindos do Zustand (lista de produtos do PDV)
   * ================================================================
   */
  const products = subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo
    ?.periodProducts?.Products ?? [];

  /**
   * ================================================================
   * Retorna um produto da lista de produtos, dado seu UUID
   * ================================================================
   */
  const getProduct = useCallback(
    (productUuid: string) => {
      return (
        products.find((product: { productUuid: string }) => {
          return product.productUuid === productUuid;
        }) || null
      );
    },
    [products]
  );

  /**
   * ================================================================
   * Filtro de produtos memoizado (por nome e código)
   * ================================================================
   */
  const filteredProducts = useMemo(() => {
    return products.filter((product: any) => {
      const matchesName =
        !productNameFilter ||
        (product.productName &&
          product.productName
            .toLowerCase()
            .startsWith(productNameFilter.toLowerCase()));

      const matchesCode =
        !productCodeFilter ||
        (product.productCode &&
          product.productCode.toString().startsWith(productCodeFilter));

      return matchesName && matchesCode;
    });
  }, [products, productNameFilter, productCodeFilter]);

  /**
   * ================================================================
   * Produto selecionado (na tabela) de acordo com a linha marcada,
   * também memoizado para evitar cálculos a cada render
   * ================================================================
   */
  const selectedProduct = useMemo(() => {
    if (selectedRow >= 0 && selectedRow < filteredProducts.length) {
      return filteredProducts[selectedRow];
    }
    return null;
  }, [filteredProducts, selectedRow]);

  /**
   * ================================================================
   * Lida com a seleção (checkbox) de um produto na tabela
   * ================================================================
   */
  const handleCheckboxChange = useCallback((index: number) => {
    setSelectedRow(index);
  }, []);

  /**
   * ================================================================
   * Fecha o modal principal e reseta estados
   * ================================================================
   */
  const handleClose = useCallback(() => {
    setShowComboModal(false);
    setCurrentComboIndex(0);
    setComboOptions([]);
    setSelectedComboOptions({});
    setQuantity(1);
    setProductCodeFilter("");
    setProductNameFilter("");
    setIsSubmitting(false);

    onRequestClose();
  }, [onRequestClose]);
  /**
   * ================================================================
   * Chamada de API para adicionar produto na ordem
   * ================================================================
   */
  const callAddProductAPI = useCallback(
    async (product: any) => {
      try {
        const productOrderData: {
          orderType: string;
          productUuid: string;
          productPrinted: boolean;
          productCode: string;
          productName: string;
          productPrice: any;
          productDiscount: string;
          peopleNumber?: string;
          productComboInfo?: { [key: string]: string };
        } = {
          orderType:
            Number(orderDataZustand.orderType) === 1
              ? "DESK"
              : Number(orderDataZustand.orderType) === 2
                ? "PICKUP"
                : "DELIVERY",
          productUuid: product.productUuid,
          productCode: String(product.productCode),
          productPrinted: false,
          productName: product.productName,
          productPrice: product.productPrice,
          productDiscount: "0",
          ...(Number(orderDataZustand.orderType) === 1 && {
            peopleNumber: people,
          }),
          ...(Object.keys(selectedComboOptions).length > 0 && {
            productComboInfo: selectedComboOptions,
          }),
        };

        await api.post(
          `addProductOrder/${orderDataZustand.orderUuid}`,
          productOrderData
        );

        handleClose();
        setMessageZustand("Produto adicionado com sucesso!", "success");
      } catch (error: any) {
        if (error.message === "Network Error") {
          setMessageZustand(
            "Erro ao tentar incluir produto, entre em contato com os administradores do Alfredo da sua unidade.",
            "error"
          );
        } else {
          setMessageZustand(`Erro ao incluir produto: ${error.message}`, "error");
        }
      }
    },
    [
      orderDataZustand.orderType,
      orderDataZustand.orderUuid,
      people,
      selectedComboOptions,
      handleClose,
      setMessageZustand,
    ]
  );
  /**
   * ================================================================
   * Ao confirmar a seleção de todos os grupos de combo, finaliza o processo
   * ================================================================
   */
  const handleAddProduct = useCallback(() => {
    if (!selectedProduct) return;
    const grupoAtual = comboOptions[currentComboIndex];

    // Verifica se alguma opção foi selecionada para o grupo atual
    if (!selectedComboOptions[grupoAtual.productGroup]) {
      setMessageZustand("Selecione uma opção antes de avançar.", "error");
      return;
    }

    const selectedOptions = comboOptions.map((group) => ({
      productGroup: group.productGroup,
      selectedOption: selectedComboOptions[group.productGroup],
    }));

    // Chama a API para adicionar o produto final
    callAddProductAPI({ ...selectedProduct, selectedOptions });
  }, [
    comboOptions,
    currentComboIndex,
    selectedComboOptions,
    selectedProduct,
    setMessageZustand,
    callAddProductAPI,
  ]);

  /**
   * ================================================================
   * Carrega as opções de combo de um produto (se houver combo)
   * ================================================================
   */
  const loadComboOptions = useCallback(async () => {
    if (!selectedProduct?.productComboInfo) return;
    const productComboInfo = selectedProduct.productComboInfo;
    const comboData: ProductGroupOptions[] = [];

    for (const group of productComboInfo) {
      const options = await Promise.all(
        group.options.map(async (uuid: string) => {
          const productObtido = await getProduct(uuid);
          return {
            productUuid: productObtido.productUuid,
            productCode: productObtido.productCode,
            productName: productObtido.productName,
            productPrice: Number(productObtido.productPrice)
              .toFixed(2)
              .replace(".", ","),
          };
        })
      );

      comboData.push({
        productGroup: group.productGroup,
        options,
      });
    }
    setComboOptions(comboData);
  }, [getProduct, selectedProduct]);

  /**
   * ================================================================
   * Lida com a seleção de uma opção no modal de combo
   * ================================================================
   */
  const handleOptionSelect = useCallback(
    (productGroup: string, productUuid: string) => {
      setSelectedComboOptions((prev) => {
        // Caso o usuário clique na opção já selecionada, remove a seleção
        if (prev[productGroup] === productUuid) {
          const updatedSelection = { ...prev };
          delete updatedSelection[productGroup];
          return updatedSelection;
        }
        return { ...prev, [productGroup]: productUuid };
      });
    },
    []
  );

  /**
   * ================================================================
   * Avança para o próximo grupo de combo (ou adiciona o produto)
   * ================================================================
   */
  const handleNext = useCallback(() => {
    if (currentComboIndex < comboOptions.length - 1) {
      const grupoAtual = comboOptions[currentComboIndex];

      // Verifica se alguma opção foi selecionada para o grupo atual
      if (!selectedComboOptions[grupoAtual.productGroup]) {
        setMessageZustand("Selecione uma opção antes de avançar.", "error");
        return;
      }

      setCurrentComboIndex((prevIndex) => prevIndex + 1);
    } else {
      // Se for o último grupo, chama a função de adicionar
      if (selectedProduct) {
        const grupoAtual = comboOptions[currentComboIndex];
        // Verifica se alguma opção foi selecionada para o grupo atual
        if (!selectedComboOptions[grupoAtual.productGroup]) {
          setMessageZustand("Selecione uma opção antes de avançar.", "error");
          return;
        }
      }
      handleAddProduct();
    }
  }, [
    currentComboIndex,
    comboOptions,
    selectedComboOptions,
    selectedProduct,
    setMessageZustand,
    handleAddProduct,
  ]);

  /**
   * ================================================================
   * Volta para o grupo de combo anterior (ou fecha o modal)
   * ================================================================
   */
  const handlePrevious = useCallback(() => {
    if (currentComboIndex === 0) {
      // Se estamos no primeiro grupo, então fechamos o modal de combo
      handleClose();
    } else {
      setCurrentComboIndex((prevIndex) => prevIndex - 1);
    }
  }, [currentComboIndex, handleClose]);




  /**
   * ================================================================
   * Função para adicionar (submeter) um produto selecionado
   * ================================================================
   */
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (selectedRow !== -1 && selectedProduct) {
      // Validação de MESAS: se a pessoa informada > número total de pessoas
      if (
        Number(orderDataZustand.orderType) === 1 &&
        Number(people) > Number(orderDataZustand.orderInfo?.peopleNumber)
      ) {
        setMessageZustand(
          `O número de pessoas nesta mesa é ${orderDataZustand.orderInfo?.peopleNumber}`,
          "error"
        );
        return;
      }

      setIsSubmitting(true);
      try {
        // Se o produto for combo, exibe o modal de combo
        for (let i = 0; i < quantity; i++) {
          if (selectedProduct.productCombo) {
            await loadComboOptions();
            setShowComboModal(true);
            return;
          } else {
            await callAddProductAPI(selectedProduct);
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [
    isSubmitting,
    selectedRow,
    selectedProduct,
    orderDataZustand.orderType,
    orderDataZustand.orderInfo?.peopleNumber,
    people,
    quantity,
    setMessageZustand,
    loadComboOptions,
    callAddProductAPI,
  ]);

  /**
   * ================================================================
   * Captura a tecla pressionada no Modal de produtos
   * ================================================================
   */
  const handleKeyDownProductModal = useCallback(
    async (event: KeyboardEvent<HTMLInputElement>) => {
      if (!filteredProducts.length) return;

      if (event.key === "Enter" && !isSubmitting) {
        setIsSubmitting(true);
        try {
          // Se o foco está no input de nome
          if (document.activeElement === productNameInputRef.current) {
            if (selectedRow >= 0 && selectedRow < filteredProducts.length) {
              setProductNameFilter(filteredProducts[selectedRow].productName);
              setProductNamePlaceholder("");
              handleCheckboxChange(-1);
              productCodeInputRef.current?.focus();
            }
          }
          // Se o foco está no input de código
          else if (document.activeElement === productCodeInputRef.current) {
            if (selectedRow >= 0 && selectedRow < filteredProducts.length) {
              setProductCodeFilter(filteredProducts[selectedRow].productCode);
              setProductCodePlaceholder("");
              quantityInputRef.current?.focus();
              quantityInputRef.current?.select();
            }
          }
          // Se o foco está no input de quantidade
          else if (document.activeElement === quantityInputRef.current) {
            if (Number(orderDataZustand.orderType) === 1) {
              peopleInputRef.current?.focus();
              peopleInputRef.current?.select();
            } else {
              await handleSubmit();
            }
          }
          // Caso já esteja tudo preenchido
          else if (
            selectedRow >= 0 &&
            selectedRow < filteredProducts.length &&
            productNameFilter !== filteredProducts[selectedRow]?.productName
          ) {
            setProductNameFilter(filteredProducts[selectedRow].productName);
            productNameInputRef.current?.focus();
          } else {
            await handleSubmit();
          }
        } finally {
          setIsSubmitting(false);
        }
      }
      // Navega para baixo na tabela
      else if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextRow = (selectedRow + 1) % filteredProducts.length;
        handleCheckboxChange(nextRow);
      }
      // Navega para cima na tabela
      else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prevRow =
          (selectedRow - 1 + filteredProducts.length) % filteredProducts.length;
        handleCheckboxChange(prevRow);
      }
      // ESC limpa os filtros
      else if (event.key === "Escape") {
        setProductCodeFilter("");
        setProductNameFilter("");
      }
    },
    [
      filteredProducts,
      isSubmitting,
      selectedRow,
      handleCheckboxChange,
      orderDataZustand.orderType,
      productNameFilter,
      handleSubmit,
    ]
  );

  /**
   * ================================================================
   * Captura a tecla pressionada no Modal de combo
   * ================================================================
   */
  const handleKeyDownComboModal = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const currentGroup = comboOptions[currentComboIndex];
      const options = currentGroup?.options;
      if (!options || options.length === 0) return;

      if (event.key === "Enter") {
        handleNext();
      } else if (event.key === "ArrowDown") {
        setSelectedIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % options.length;
          handleOptionSelect(currentGroup.productGroup, options[newIndex].productUuid);
          return newIndex;
        });
      } else if (event.key === "ArrowUp") {
        setSelectedIndex((prevIndex) => {
          const newIndex = (prevIndex - 1 + options.length) % options.length;
          handleOptionSelect(currentGroup.productGroup, options[newIndex].productUuid);
          return newIndex;
        });
      } else if (event.key === "Escape") {
        event.stopPropagation();
        handlePrevious();
      }
    },
    [
      comboOptions,
      currentComboIndex,
      handleNext,
      handleOptionSelect,
      handlePrevious,
    ]
  );

  /**
   * ================================================================
   * useEffect: ao alterar o currentGroup, se houver opções, seleciona a primeira
   * ================================================================
   */
  useEffect(() => {
    if (comboOptions[currentComboIndex]?.options?.length) {
      const firstOption = comboOptions[currentComboIndex].options[0];
      handleOptionSelect(
        comboOptions[currentComboIndex].productGroup,
        firstOption.productUuid
      );
      setSelectedIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboOptions, currentComboIndex]);

  /**
   * ================================================================
   * useEffect: quando abre o modal principal e não está no comboModal,
   *            seleciona a primeira linha se existir produto
   * ================================================================
   */
  useEffect(() => {
    if (isOpen && !showComboModal) {
      if (filteredProducts.length > 0 && selectedRow === -1) {
        handleCheckboxChange(0);
      }

      // Atualiza placeholders se houver produtos
      if (
        filteredProducts.length > 0 &&
        selectedRow >= 0 &&
        selectedRow < filteredProducts.length
      ) {
        const { productName, productCode } = filteredProducts[selectedRow];
        setProductNamePlaceholder(productName);
        setProductCodePlaceholder(productCode);
      } else {
        setProductNamePlaceholder("");
        setProductCodePlaceholder("");
      }
    }
  }, [
    isOpen,
    showComboModal,
    filteredProducts,
    selectedRow,
    handleCheckboxChange,
  ]);

  /**
   * ================================================================
   * useEffect: focar no input de nome assim que o modal principal abrir
   * ================================================================
   */
  useEffect(() => {
    if (isOpen && !showComboModal) {
      const focusTimeout = setTimeout(() => {
        modalWrapperRef.current?.focus();
        productNameInputRef.current?.focus();
      }, 500);
      return () => clearTimeout(focusTimeout);
    }
  }, [isOpen, showComboModal]);

  /**
   * ================================================================
   * useEffect: quando o modal de combo abrir, focar nele
   * ================================================================
   */
  useEffect(() => {
    if (showComboModal) {
      const focusTimeout = setTimeout(() => {
        modalComboWrapperRef.current?.focus();
      }, 50);
      return () => clearTimeout(focusTimeout);
    }
  }, [showComboModal]);

  /**
   * ================================================================
   * useEffect: ao mudar a linha selecionada, rola a tabela automaticamente
   * ================================================================
   */
  useEffect(() => {
    if (selectedRow >= 0 && rowRefs.current[selectedRow]) {
      rowRefs.current[selectedRow]?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [selectedRow]);

  /**
   * ================================================================
   * Memo para a renderização das linhas da tabela
   * ================================================================
   */
  const productRows = useMemo(() => {
    return filteredProducts.map((product: any, index: number) => (
      <Styles.TableRow
        key={product.productUuid}
        isSelected={index === selectedRow}
        ref={(el) => (rowRefs.current[index] = el)}
        onClick={() => handleCheckboxChange(index)}
      >
        <td>
          <input
            type="checkbox"
            checked={index === selectedRow}
            onChange={() => handleCheckboxChange(index)}
          />
        </td>
        <td>{product.productCode}</td>
        <td>{product.productName}</td>
        <td>{product.productPrice}</td>
      </Styles.TableRow>
    ));
  }, [filteredProducts, selectedRow, handleCheckboxChange]);

  /**
   * ================================================================
   * Renderização do componente
   * ================================================================
   */
  return (
    <>
      {/* Modal principal de adicionar produto */}
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        style={Styles.customStyles}
        contentLabel="Main Modal"
        ariaHideApp={false}
      >
        <Styles.ModalWrapper
          tabIndex={-1}
          ref={modalWrapperRef}
          onKeyDown={handleKeyDownProductModal}
        >
          <Styles.CloseButton
            onClick={() => {
              setProductCodeFilter("");
              setProductNameFilter("");
              handleClose();
            }}
          >
            &times;
          </Styles.CloseButton>

          <Styles.ModalContent>
            <Styles.ModalHeader
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .buttonsColor
              }
            >
              <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
              <Styles.ModalTitle>Adicione Produtos à Conta</Styles.ModalTitle>
              <Styles.ModalDescription>
                {`Informe os produtos e suas quantidades`}
              </Styles.ModalDescription>
            </Styles.ModalHeader>

            <Styles.ModalBody
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .backgroundColor
              }
            >
              <Styles.RowContainer>
                <Styles.FieldLabel>
                  Digite o Nome do Produto:
                </Styles.FieldLabel>
                <Styles.FieldContainer>
                  <Styles.TextInput
                    color={
                      subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                        .buttonsColor
                    }
                  >
                    <input
                      ref={productNameInputRef}
                      type="text"
                      autoComplete="off"
                      id="product-name-input"
                      value={productNameFilter}
                      autoFocus={true}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setProductNameFilter(e.target.value.toUpperCase())
                      }
                      style={{
                        backgroundColor: "transparent",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                    <input
                      type="text"
                      value={productNamePlaceholder}
                      id="product-name-suggestion"
                      disabled
                      style={{
                        color: "#999",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        zIndex: 1,
                      }}
                    />
                  </Styles.TextInput>
                </Styles.FieldContainer>
              </Styles.RowContainer>

              <Styles.RowContainer>
                <Styles.FieldLabel>
                  Digite o Código do Produto:
                </Styles.FieldLabel>
                <Styles.FieldContainer>
                  <Styles.TextInput
                    color={
                      subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                        .buttonsColor
                    }
                  >
                    <input
                      ref={productCodeInputRef}
                      type="text"
                      id="product-code-input"
                      value={productCodeFilter}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setProductCodeFilter(e.target.value)
                      }
                      style={{
                        backgroundColor: "transparent",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                    <input
                      type="text"
                      value={productCodePlaceholder}
                      id="product-code-suggestion"
                      disabled
                      style={{
                        color: "#999",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        zIndex: 1,
                      }}
                    />
                  </Styles.TextInput>
                </Styles.FieldContainer>
              </Styles.RowContainer>

              <Styles.RowContainer>
                <Styles.FieldLabel>
                  Informe a Quantidade do Produto:
                </Styles.FieldLabel>
                <Styles.FieldContainer>
                  <Styles.NumberInput
                    color={
                      subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                        .buttonsColor
                    }
                  >
                    <input
                      ref={quantityInputRef}
                      type="number"
                      placeholder=""
                      value={quantity}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setQuantity(Number(e.target.value))
                      }
                    />
                  </Styles.NumberInput>
                </Styles.FieldContainer>
              </Styles.RowContainer>

              {Number(orderDataZustand.orderType) === 1 && (
                <Styles.RowContainer>
                  <Styles.FieldLabel>
                    Informe para quem é este Produto:
                  </Styles.FieldLabel>
                  <Styles.FieldContainer>
                    <Styles.NumberInput
                      color={
                        subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                          .buttonsColor
                      }
                    >
                      <input
                        ref={peopleInputRef}
                        type="number"
                        placeholder=""
                        value={people}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setPeople(e.target.value)
                        }
                      />
                    </Styles.NumberInput>
                    <Styles.Unit
                      color={
                        subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                          .buttonsColor
                      }
                    >
                      Pessoa
                    </Styles.Unit>
                  </Styles.FieldContainer>
                </Styles.RowContainer>
              )}

              <Styles.RowContainer>
                <Styles.TableContainer ref={tableRef}>
                  <Styles.ProductsTable>
                    <thead>
                      <tr>
                        <th style={{ width: "20%" }}>Selecione</th>
                        <th style={{ width: "20%" }}>Código</th>
                        <th style={{ width: "40%" }}>Nome</th>
                        <th style={{ width: "20%" }}>Preço</th>
                      </tr>
                    </thead>
                    <tbody>{productRows}</tbody>
                  </Styles.ProductsTable>
                </Styles.TableContainer>
              </Styles.RowContainer>
            </Styles.ModalBody>

            <Styles.ModalFooter
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .buttonsColor
              }
            >
              <Styles.Button
                onClick={() => {
                  handleClose();
                }}
              >
                Cancelar
              </Styles.Button>

              <Styles.Button onClick={handleSubmit}>Adicionar Produto</Styles.Button>
            </Styles.ModalFooter>
          </Styles.ModalContent>
        </Styles.ModalWrapper>
      </Modal>

      {/* Modal secundário (para combos) */}
      <Modal
        isOpen={showComboModal}
        onRequestClose={handleClose}
        style={Styles.customStyles}
        contentLabel="Combo Modal"
        ariaHideApp={false}
      >
        <Styles.ModalWrapper
          tabIndex={-1}
          ref={modalComboWrapperRef}
          onKeyDown={handleKeyDownComboModal}
        >
          <Styles.CloseButton
            onClick={() => {
              setProductCodeFilter("");
              setProductNameFilter("");
              handleClose();
            }}
          >
            &times;
          </Styles.CloseButton>

          <AlfredoComponents.MessageBox />

          <Styles.ModalContent>
            <Styles.ModalHeader
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .buttonsColor
              }
            >
              <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
              <Styles.ModalTitle>
                {comboOptions[currentComboIndex]?.productGroup || "Selecione"}
              </Styles.ModalTitle>
              <Styles.ModalDescription>
                Escolha a opção desejada
              </Styles.ModalDescription>
            </Styles.ModalHeader>

            <Styles.ModalBody
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .backgroundColor
              }
            >
              <Styles.RowContainer>
                <Styles.TableContainer>
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nome</th>
                        <th>Preço</th>
                        <th>Selecionar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comboOptions[currentComboIndex]?.options?.map(
                        (option, index) => (
                          <tr
                            key={option.productUuid}
                            onClick={() =>
                              handleOptionSelect(
                                comboOptions[currentComboIndex].productGroup,
                                option.productUuid
                              )
                            }
                            style={{
                              backgroundColor:
                                index === selectedIndex ? "#e0f7fa" : "transparent",
                            }}
                          >
                            <td>{option.productCode}</td>
                            <td>{option.productName}</td>
                            <td>{option.productPrice}</td>
                            <td>
                              <Styles.SelectButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOptionSelect(
                                    comboOptions[currentComboIndex].productGroup,
                                    option.productUuid
                                  );
                                }}
                                isSelected={
                                  selectedComboOptions[
                                  comboOptions[currentComboIndex].productGroup
                                  ] === option.productUuid
                                }
                              >
                                {selectedComboOptions[
                                  comboOptions[currentComboIndex].productGroup
                                ] === option.productUuid
                                  ? "✅"
                                  : "🟦"}
                              </Styles.SelectButton>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </Styles.TableContainer>
              </Styles.RowContainer>
            </Styles.ModalBody>

            <Styles.ModalFooter
              color={
                subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                  .buttonsColor
              }
            >
              <Styles.Button onClick={handlePrevious}>Voltar</Styles.Button>
              {currentComboIndex < comboOptions.length - 1 ? (
                <Styles.Button onClick={handleNext}>Próximo</Styles.Button>
              ) : (
                <Styles.Button onClick={handleNext}>
                  Adicionar Produto
                </Styles.Button>
              )}
            </Styles.ModalFooter>
          </Styles.ModalContent>
        </Styles.ModalWrapper>
      </Modal>
    </>
  );
};

// Memoiza o componente inteiro para evitar renders desnecessários
export default React.memo(AddProductOrderModal);
